import { type FC, useState } from "react"

import { replaceArrayValue, toggleArrayItem } from "@forento/shared/utilities/array"

import { useAlert } from "~/contexts/AlertContext"
import Button from "~/themes/original/components/Button"
import Checkbox, { Radio } from "~/themes/original/components/Checkbox"
import { useTranslation } from "~/translations"
import { query } from "~/utilities/trpc"

type ExamQuestion = {
	label: string
	isSingleChoice: boolean
	options: { id: number; label: string }[]
}

type Props = { examId: number; questions: ExamQuestion[]; reload(): Promise<unknown> }
const Content: FC<Props> = ({ examId, questions, reload }) => {
	const alert = useAlert()
	const t = useTranslation()

	const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
	const [answers, setAnswers] = useState<number[][]>([])

	const completeExam = query.course.exam.completeExam.useMutation({
		onSuccess: reload,
		async onError(error) {
			console.error(error)
			await alert.show(t("general.error"), t("course.exam.completeFailed"))
		},
	})

	const question = questions[currentQuestionIndex]

	function setSelectedOptions(options: number[]) {
		setAnswers(current => replaceArrayValue(current, currentQuestionIndex, options))
	}

	async function handleComplete() {
		const dialog = await alert.confirm(t("course.exam.complete"), t("course.exam.completeConfirm"))
		if (!dialog.result) return

		completeExam.mutate({ examId, answers }, { onSettled: dialog.close })
	}

	const currentQuestionAnswer = answers[currentQuestionIndex] ?? []
	const isNextDisabled = currentQuestionAnswer.length === 0

	return (
		<div className="flex flex-col gap-4">
			{question.isSingleChoice ? (
				<SingleChoiceQuestion
					question={question}
					selectedOptions={currentQuestionAnswer}
					setSelectedOptions={setSelectedOptions}
				/>
			) : (
				<MultipleChoiceQuestion
					question={question}
					selectedOptions={currentQuestionAnswer}
					setSelectedOptions={setSelectedOptions}
				/>
			)}
			<div className="flex gap-4">
				<Button
					variant="secondary"
					onClick={() => setCurrentQuestionIndex(currentQuestionIndex - 1)}
					isDisabled={currentQuestionIndex === 0}
				>
					{t("course.exam.previous")}
				</Button>
				{currentQuestionIndex < questions.length - 1 ? (
					<Button
						variant="primary"
						onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
						isDisabled={isNextDisabled}
					>
						{t("course.exam.next")}
					</Button>
				) : (
					<Button variant="primary" onClick={handleComplete} isDisabled={isNextDisabled}>
						{t("course.exam.complete")}
					</Button>
				)}
			</div>
		</div>
	)
}

type QuestionProps = {
	question: ExamQuestion
	selectedOptions: number[]
	setSelectedOptions(value: number[]): void
}
const SingleChoiceQuestion: FC<QuestionProps> = ({ question, selectedOptions, setSelectedOptions }) => (
	<>
		<h2 className="mb-2 text-xl font-bold">{question.label}</h2>
		<div className="mb-6 flex flex-col gap-4">
			{question.options.map(option => (
				<Radio
					key={option.id}
					label={option.label}
					isChecked={selectedOptions.includes(option.id)}
					onChange={() => setSelectedOptions(!selectedOptions.includes(option.id) ? [option.id] : [])}
				/>
			))}
		</div>
	</>
)

const MultipleChoiceQuestion: FC<QuestionProps> = ({ question, selectedOptions, setSelectedOptions }) => {
	const t = useTranslation()

	return (
		<>
			<h2 className="mb-2 text-xl font-bold">{question.label}</h2>
			<p className="mb-2 text-sm text-light">{t("course.exam.multipleChoice")}</p>
			<div className="mb-6 flex flex-col gap-4">
				{question.options.map(option => (
					<Checkbox
						key={option.id}
						label={option.label}
						isChecked={selectedOptions.includes(option.id)}
						onChange={() => setSelectedOptions(toggleArrayItem(selectedOptions, option.id))}
					/>
				))}
			</div>
		</>
	)
}

export default Content
